import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Sparkles, Zap, Crown } from "lucide-react";

const Pricing = () => {
  const { language, getPrice, formatPrice, currency } = useLanguage();
  const isGerman = language === "de";

  const packages = [
    {
      name: isGerman ? "Starter" : "Starter",
      price: getPrice("starter"),
      videos: 3,
      icon: Zap,
      color: "from-neon-cyan to-blue-500",
      features: isGerman
        ? [
            "3 AI UGC Videos",
            "3 Poster/Thumbnails",
            "Captions inklusive",
            "72 Stunden Lieferung",
            "1 Revisionsrunde",
            "Kommerzielle Rechte",
          ]
        : [
            "3 AI UGC Videos",
            "3 Posters/Thumbnails",
            "Captions included",
            "72-hour delivery",
            "1 revision round",
            "Commercial rights",
          ],
      bestFor: isGerman ? "Ideal zum Testen" : "Perfect for testing",
    },
    {
      name: isGerman ? "Launch" : "Launch",
      price: getPrice("launch"),
      videos: 6,
      icon: Sparkles,
      color: "from-neon-purple via-neon-pink to-orange-500",
      popular: true,
      features: isGerman
        ? [
            "6 AI UGC Videos",
            "6 Poster/Thumbnails",
            "Captions inklusive",
            "72 Stunden Lieferung",
            "1 Revisionsrunde",
            "Kommerzielle Rechte",
            "Prioritäts-Support",
          ]
        : [
            "6 AI UGC Videos",
            "6 Posters/Thumbnails",
            "Captions included",
            "72-hour delivery",
            "1 revision round",
            "Commercial rights",
            "Priority support",
          ],
      bestFor: isGerman ? "Beliebteste Wahl" : "Most popular choice",
    },
    {
      name: isGerman ? "Scale" : "Scale",
      price: getPrice("scale"),
      videos: 12,
      icon: Crown,
      color: "from-amber to-orange-500",
      features: isGerman
        ? [
            "12 AI UGC Videos",
            "12 Poster/Thumbnails",
            "Captions inklusive",
            "72 Stunden Lieferung",
            "2 Revisionsrunden",
            "Kommerzielle Rechte",
            "VIP Support",
            "Strategie-Call",
          ]
        : [
            "12 AI UGC Videos",
            "12 Posters/Thumbnails",
            "Captions included",
            "72-hour delivery",
            "2 revision rounds",
            "Commercial rights",
            "VIP support",
            "Strategy call",
          ],
      bestFor: isGerman ? "Maximales Wachstum" : "Maximum growth",
    },
  ];

  const addOns = isGerman
    ? [
        { name: "Express Delivery (24h)", price: "+49€" },
        { name: "Extra Revisionsrunde", price: "+29€" },
        { name: "Voiceover (andere Sprache)", price: "+19€/Video" },
        { name: "Längere Videos (60s+)", price: "+39€/Video" },
      ]
    : [
        { name: "Express Delivery (24h)", price: "+$49" },
        { name: "Extra Revision Round", price: "+$29" },
        { name: "Voiceover (different language)", price: "+$19/video" },
        { name: "Longer Videos (60s+)", price: "+$39/video" },
      ];

  const comparison = isGerman
    ? [
        { feature: "Kosten pro Video", fuel72: "~50€", traditional: "500-2000€", influencer: "300-1000€" },
        { feature: "Lieferzeit", fuel72: "72 Stunden", traditional: "2-4 Wochen", influencer: "1-3 Wochen" },
        { feature: "Skalierbarkeit", fuel72: "Unbegrenzt", traditional: "Begrenzt", influencer: "Begrenzt" },
        { feature: "Konsistenz", fuel72: "100%", traditional: "Variiert", influencer: "Variiert" },
        { feature: "Revisionen", fuel72: "Inklusive", traditional: "Extra Kosten", influencer: "Extra Kosten" },
        { feature: "Kein Filmen nötig", fuel72: "✓", traditional: "✗", influencer: "✗" },
      ]
    : [
        { feature: "Cost per video", fuel72: "~$50", traditional: "$500-2000", influencer: "$300-1000" },
        { feature: "Delivery time", fuel72: "72 hours", traditional: "2-4 weeks", influencer: "1-3 weeks" },
        { feature: "Scalability", fuel72: "Unlimited", traditional: "Limited", influencer: "Limited" },
        { feature: "Consistency", fuel72: "100%", traditional: "Varies", influencer: "Varies" },
        { feature: "Revisions", fuel72: "Included", traditional: "Extra cost", influencer: "Extra cost" },
        { feature: "No filming needed", fuel72: "✓", traditional: "✗", influencer: "✗" },
      ];

  const pricingSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Fuel72 AI UGC Video Packages",
    "description": isGerman
      ? "AI UGC Video Pakete für Unternehmen. 3, 6 oder 12 Videos mit Poster und Captions."
      : "AI UGC Video packages for businesses. 3, 6 or 12 videos with posters and captions.",
    "brand": { "@type": "Brand", "name": "Fuel72" },
    "offers": packages.map((pkg) => ({
      "@type": "Offer",
      "name": pkg.name,
      "price": pkg.price,
      "priceCurrency": isGerman ? "EUR" : "USD",
      "availability": "https://schema.org/InStock",
      "description": `${pkg.videos} AI UGC Videos + ${pkg.videos} Posters + Captions`,
    })),
  };

  return (
    <>
      <Helmet>
        <title>
          {isGerman
            ? "Preise | AI UGC Video Pakete | Ab 149€ | Fuel72"
            : "Pricing | AI UGC Video Packages | From $149 | Fuel72"}
        </title>
        <meta
          name="description"
          content={
            isGerman
              ? "Fuel72 Preise: Starter 149€ (3 Videos), Launch 297€ (6 Videos), Scale 497€ (12 Videos). Alle Pakete mit Poster, Captions & 72h Lieferung."
              : "Fuel72 Pricing: Starter $149 (3 videos), Launch $297 (6 videos), Scale $497 (12 videos). All packages include posters, captions & 72h delivery."
          }
        />
        <meta
          name="keywords"
          content={
            isGerman
              ? "fuel72 preise, ugc kosten, ai ugc preis, ugc video kosten, ugc pakete, fuel72 kosten"
              : "fuel72 pricing, ugc cost, ai ugc price, ugc video cost, ugc packages, fuel72 cost"
          }
        />
        <link rel="canonical" href="https://www.fuel72.com/pricing" />
        <link rel="alternate" hrefLang="en" href="https://www.fuel72.com/pricing" />
        <link rel="alternate" hrefLang="de" href="https://www.fuel72.com/pricing?lang=de" />
        <meta property="og:title" content={isGerman ? "Preise | Fuel72" : "Pricing | Fuel72"} />
        <meta property="og:url" content="https://www.fuel72.com/pricing" />
        <script type="application/ld+json">{JSON.stringify(pricingSchema)}</script>
      </Helmet>

      <div className="min-h-screen gradient-mesh">
        <Navbar />

        <main className="container mx-auto px-4 py-20 md:py-28">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
              {isGerman ? (
                <>
                  Transparente{" "}
                  <span className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 bg-clip-text text-transparent">
                    Preise
                  </span>
                </>
              ) : (
                <>
                  Transparent{" "}
                  <span className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 bg-clip-text text-transparent">
                    Pricing
                  </span>
                </>
              )}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {isGerman
                ? "Keine versteckten Gebühren. Keine Abos. Einmalzahlung für professionelle AI UGC Videos."
                : "No hidden fees. No subscriptions. One-time payment for professional AI UGC videos."}
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20">
            {packages.map((pkg, index) => (
              <div
                key={index}
                className={`relative glass-morphism border rounded-2xl p-8 ${
                  pkg.popular
                    ? "border-neon-purple/50 scale-105 shadow-[0_0_40px_rgba(168,85,247,0.3)]"
                    : "border-white/10"
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-neon-purple to-neon-pink rounded-full text-sm font-bold text-white">
                    {isGerman ? "Beliebteste" : "Most Popular"}
                  </div>
                )}
                <div className="text-center mb-8">
                  <div
                    className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${pkg.color} flex items-center justify-center`}
                  >
                    <pkg.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-heading font-bold mb-2">{pkg.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{pkg.bestFor}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold">{currency}{pkg.price}</span>
                    <span className="text-muted-foreground">
                      {isGerman ? "einmalig" : "one-time"}
                    </span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-neon-green flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  className={`w-full ${
                    pkg.popular
                      ? "bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 text-white"
                      : ""
                  }`}
                  variant={pkg.popular ? "default" : "outline"}
                >
                  <Link to="/order">
                    {isGerman ? "Jetzt bestellen" : "Order Now"}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </div>
            ))}
          </div>

          {/* Add-ons */}
          <div className="max-w-4xl mx-auto mb-20">
            <h2 className="text-3xl font-heading font-bold text-center mb-8">
              {isGerman ? "Optionale Add-ons" : "Optional Add-ons"}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {addOns.map((addon, index) => (
                <div
                  key={index}
                  className="glass-morphism border border-white/10 rounded-xl p-4 flex justify-between items-center"
                >
                  <span>{addon.name}</span>
                  <span className="font-bold text-neon-purple">{addon.price}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison Table */}
          <div className="max-w-5xl mx-auto mb-20">
            <h2 className="text-3xl font-heading font-bold text-center mb-8">
              {isGerman ? "Vergleich" : "Comparison"}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full glass-morphism border border-white/10 rounded-2xl overflow-hidden">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="p-4 text-left"></th>
                    <th className="p-4 text-center bg-neon-purple/10">
                      <span className="font-bold text-neon-purple">Fuel72</span>
                    </th>
                    <th className="p-4 text-center">
                      {isGerman ? "Trad. UGC" : "Traditional UGC"}
                    </th>
                    <th className="p-4 text-center">Influencer</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, index) => (
                    <tr key={index} className="border-b border-white/10 last:border-b-0">
                      <td className="p-4 font-medium">{row.feature}</td>
                      <td className="p-4 text-center bg-neon-purple/5 font-bold text-neon-green">
                        {row.fuel72}
                      </td>
                      <td className="p-4 text-center text-muted-foreground">{row.traditional}</td>
                      <td className="p-4 text-center text-muted-foreground">{row.influencer}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 text-white font-bold px-12 py-6 text-lg"
            >
              <Link to="/order">
                {isGerman ? "Jetzt starten" : "Get Started Now"}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>

          {/* Internal Links */}
          <div className="mt-20 pt-10 border-t border-white/10 text-center">
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/faq" className="text-neon-purple hover:underline">FAQ</Link>
              <Link to={isGerman ? "/ugc-kaufen" : "/buy-ugc"} className="text-neon-purple hover:underline">
                {isGerman ? "UGC kaufen" : "Buy UGC"}
              </Link>
              <Link to="/blog" className="text-neon-purple hover:underline">Blog</Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Pricing;